import React from 'react';
import { useTranslation } from 'react-i18next';
import { AssetDetailsResponse } from '../../../api/asset/assetApiTypes';
import InputField from '../../../components/ui/InputField';
import AssetDetailSection from './AssetDetailSection';
import DetailField from './DetailField';
import { serviceTypeLabel } from '../../../utils/useCaseLabels';

interface BasicInfoSectionProps {
    /** Omitted in create mode. */
    data?: AssetDetailsResponse;
    mode: 'view' | 'edit' | 'create';
    formName?: string;
    onNameChange?: (name: string) => void;
    /**
     * Optional venue/building designation ("Stadthalle", "Schloss"). Create mode
     * only. Substitutes the street part when the object name is composed, so a
     * named venue reads as "Stadthalle, 2. OG | Bad Krozingen" instead of
     * repeating the street. Not a second name field — the composed name below
     * stays the editable source of truth.
     */
    designation?: string;
    onDesignationChange?: (designation: string) => void;
    /** Explicit use case for create mode when `data` is not available. */
    compartmentTypes?: string[];
    /** The product's category as the tenant names it (#3167); else the use case. */
    serviceName?: string;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
    data,
    mode,
    formName,
    onNameChange,
    designation,
    onDesignationChange,
    compartmentTypes,
    serviceName,
}) => {
    const { t } = useTranslation();
    const isCreating = mode === 'create';
    const isEditable = mode === 'edit' || isCreating;

    const useCases = compartmentTypes ?? data?.Group?.compartmentTypes ?? [];
    // Each use case's name (service_type_selector.<useCase>, hall included);
    // an unknown value shows as itself, as before.
    const useCaseDisplay = serviceName ?? useCases
        .map((uc) => serviceTypeLabel(t, uc))
        .join(', ');

    return (
        <AssetDetailSection title={t('asset_detail.sections.basic_info')}>
            {isCreating && onDesignationChange && (
                <div className={'mb-4'}>
                    <InputField
                        label={t('asset_detail.fields.designation')}
                        help={t('asset_detail.fields.designation_help')}
                        labelAbove={true}
                        placeholder={t('asset_detail.fields.designation_placeholder')}
                        value={designation ?? ''}
                        onChange={onDesignationChange}
                    />
                </div>
            )}
            <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                {isEditable ? (
                    <InputField
                        // Required in both modes: AssetDetailPage now enforces a
                        // non-empty name on save, matching create (#1749).
                        label={`${t('asset_detail.fields.name')} *`}
                        value={formName ?? ''}
                        onChange={(val) => onNameChange?.(val)}
                        placeholder={isCreating ? t('asset_create.name_placeholder') : undefined}
                    />
                ) : (
                    <DetailField
                        label={t('asset_detail.fields.name')}
                        value={data?.name}
                    />
                )}
                {/* Read-only everywhere: it is the product's (#3167), chosen
                    with the product, never on its own. Named as the product
                    category — the use case is only its template (ADR-0026;
                    PO, 2026-10-23). */}
                {useCaseDisplay ? (
                    <DetailField
                        label={t('asset_detail.product.category_label')}
                        value={useCaseDisplay}
                    />
                ) : null}
            </div>
        </AssetDetailSection>
    );
};

export default BasicInfoSection;
